import { FC, useEffect } from "react"
import { useDispatch } from "react-redux"
import { Button, Chip, CopyButton, Loader, Text } from "dash-styled"
import { ReactComponent as IconSpinner } from "icons/lib/spinner.svg"
import { ReactComponent as MailIcon } from "icons/lib/mail.svg"
import { ReactComponent as TrashIcon } from "icons/lib/trash.svg"

import {
    createInbox,
    deleteInbox,
    sendInboxVerification,
    startPollVerifyInbox,
    stopPollVerifyInbox
} from "../../../../../../../Actions/API/Inbox/InboxActions"
import { useBoolean } from "../../../../../../../Hooks/useBoolean"
import { useInbox } from "../../../../../../../Hooks/useInbox"
import { Banner } from "../../../../../../Common/Banner/Banner"

interface EmailInboxProps {
    appID: string
}

export const EmailInbox: FC<EmailInboxProps> = ({ appID }) => {
    const dispatch = useDispatch()
    const { inbox, isVerified } = useInbox(appID)
    const [creating, startCreation, endCreation] = useBoolean(false)

    useEffect(() => {
        if (!isVerified) {
            dispatch(startPollVerifyInbox(appID))
        } else {
            dispatch(stopPollVerifyInbox(appID))
        }
        return () => {
            dispatch(stopPollVerifyInbox(appID))
        }
    }, [appID, isVerified, dispatch])

    const handleCreate = async () => {
        startCreation()
        await dispatch(createInbox(appID))
        endCreation()
    }

    const handleSendVerification = () => {
        dispatch(sendInboxVerification(appID))
    }

    const handleDelete = () => {
        dispatch(deleteInbox(appID))
    }

    return (
        <>
            <Banner icon="info" className="mt-4">
                Here you can setup your dedicated email inbox for this project.
                Once setup you can send emails with attachments to the address
                shown below.
            </Banner>
            {inbox === null ? (
                <Button
                    size="md"
                    className="w-64 my-2 mx-auto flex items-center justify-center"
                    onClick={handleCreate}
                >
                    {creating ? (
                        <>
                            Creating <IconSpinner className="w-4 h-4 mx-2" />
                        </>
                    ) : (
                        "Create new inbox"
                    )}
                </Button>
            ) : inbox ? (
                <div className="flex items-center justify-center flex-wrap">
                    <Text>{inbox.address}</Text>
                    <CopyButton
                        value={inbox.address}
                        variant="withText"
                        className="w-auto mx-2 h-9"
                        text="Copy to clipboard"
                    />
                    <Button
                        disabled={isVerified || isVerified === undefined}
                        onClick={handleSendVerification}
                        size="xs"
                        color="white"
                        variant="contained2"
                        className="w-auto flex h-9 mx-2"
                    >
                        <MailIcon className="mr-2" />
                        Send verification request
                    </Button>
                    <Button
                        onClick={handleDelete}
                        size="xs"
                        color="white"
                        variant="contained2"
                        className="w-auto flex h-9 mx-2"
                    >
                        <TrashIcon className="mr-2" />
                        Delete inbox
                    </Button>
                    {isVerified !== undefined && (
                        <Chip size="md" color={isVerified ? "green" : "gray"}>
                            {isVerified ? "Verified!" : "Pending"}
                        </Chip>
                    )}
                </div>
            ) : (
                <Loader />
            )}
        </>
    )
}
